import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trash, Warning } from "@phosphor-icons/react";

/**
 * Delete controls for an admin CRM page. Renders two buttons + two modals.
 *
 *  - "Delete Selected (N)"  → POST {bulkEndpoint} { ids: [...] }   (any admin)
 *  - "Delete All"           → DELETE {allEndpoint} { confirm: "DELETE" }   (super admin only)
 *
 * Props:
 *   resource         "leads" | "requirements"        — used only in display copy
 *   selectedIds      Set<string>                     — current selection
 *   totalCount       number                          — total rows in collection (for delete-all preview)
 *   bulkEndpoint     "/admin/vehicles/bulk-delete"   — POST
 *   allEndpoint      "/admin/vehicles/all"           — DELETE
 *   onDeleted        (deleted:number, mode) => void  — caller refreshes list + clears selection
 *   testidPrefix     "leads" | "reqs"
 */
export default function AdminDeleteControls({
  resource,
  selectedIds,
  totalCount,
  bulkEndpoint,
  allEndpoint,
  onDeleted,
  testidPrefix,
}) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;

  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmAll, setConfirmAll]   = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedCount = selectedIds.size;
  const noun = resource === "requirements" ? "requirement" : "lead";

  const runBulk = async () => {
    if (selectedCount === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(bulkEndpoint, { ids: Array.from(selectedIds) });
      toast.success(`Deleted ${data.deleted} ${noun}${data.deleted === 1 ? "" : "s"}`);
      setConfirmBulk(false);
      onDeleted?.(data.deleted, "selected");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Delete failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const runAll = async () => {
    if (confirmText !== "DELETE") return;
    setBusy(true);
    try {
      const { data } = await api.delete(allEndpoint, { data: { confirm: "DELETE" } });
      toast.success(`Deleted all ${data.deleted} ${noun}${data.deleted === 1 ? "" : "s"}`);
      setConfirmAll(false);
      setConfirmText("");
      onDeleted?.(data.deleted, "all");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Delete all failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`${testidPrefix}-delete-selected-btn`}
        onClick={() => setConfirmBulk(true)}
        disabled={selectedCount === 0}
        className="flex items-center gap-1.5 text-[10px] py-2 px-3 font-display tracking-widest uppercase border border-[#FF1E1E]/55 text-[#FF1E1E] hover:bg-[#FF1E1E]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title={selectedCount === 0 ? `Select ${noun}s first` : `Delete ${selectedCount} ${noun}(s)`}
      >
        <Trash size={12} weight="bold" /> Delete Selected
        {selectedCount > 0 && <span className="font-mono-data ml-0.5">({selectedCount})</span>}
      </button>

      {isSuperAdmin && (
        <button
          data-testid={`${testidPrefix}-delete-all-btn`}
          onClick={() => { setConfirmAll(true); setConfirmText(""); }}
          className="flex items-center gap-1.5 text-[10px] py-2 px-3 font-display tracking-widest uppercase bg-[#FF1E1E]/15 border border-[#FF1E1E] text-[#FF1E1E] hover:bg-[#FF1E1E]/25 transition-colors"
          title="Super Admin only — permanently delete every record"
        >
          <Warning size={12} weight="bold" /> Delete All
        </button>
      )}

      {/* ----- Delete Selected modal ----- */}
      {confirmBulk && (
        <ConfirmShell onClose={() => !busy && setConfirmBulk(false)}>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF1E1E]/15 flex items-center justify-center">
              <Trash size={18} weight="fill" className="text-[#FF1E1E]" />
            </div>
            <div>
              <div className="font-display text-base text-white mb-1">
                Delete {selectedCount} {noun}{selectedCount === 1 ? "" : "s"}?
              </div>
              <div className="text-[12px] text-white/55 leading-relaxed">
                Records will be <span className="text-[#FF1E1E] font-semibold">permanently removed</span> from the database
                {resource === "leads" ? " along with their offers, matches and inspections" : " along with their matches"}.
                This action cannot be undone.
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              data-testid={`${testidPrefix}-delete-selected-cancel`}
              onClick={() => setConfirmBulk(false)}
              disabled={busy}
              className="btn-secondary text-[11px] py-2 px-4"
            >Cancel</button>
            <button
              data-testid={`${testidPrefix}-delete-selected-confirm`}
              onClick={runBulk}
              disabled={busy}
              className="btn-primary text-[11px] py-2 px-4 bg-[#FF1E1E] border-[#FF1E1E]"
            >
              {busy ? "Deleting…" : `Delete ${selectedCount}`}
            </button>
          </div>
        </ConfirmShell>
      )}

      {/* ----- Delete All modal ----- */}
      {confirmAll && (
        <ConfirmShell onClose={() => !busy && setConfirmAll(false)}>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF1E1E]/15 flex items-center justify-center">
              <Warning size={18} weight="fill" className="text-[#FF1E1E]" />
            </div>
            <div>
              <div className="font-display text-base text-white mb-1">Delete ALL {noun}s</div>
              <div className="text-[12px] text-white/65 leading-relaxed">
                This will permanently remove all <span className="text-white font-mono-data">{totalCount.toLocaleString()}</span> {noun}{totalCount === 1 ? "" : "s"} from the database
                {resource === "leads" ? ", including their offers, matches and inspections" : ", including their matches"}.
                <br />
                <span className="text-[#FF1E1E] font-semibold">There is no undo.</span>
              </div>
            </div>
          </div>

          <label className="block mb-4">
            <span className="qd-label">Type DELETE to confirm</span>
            <input
              data-testid={`${testidPrefix}-delete-all-input`}
              className="qd-input-block uppercase tracking-widest font-mono-data"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="DELETE"
              disabled={busy}
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              data-testid={`${testidPrefix}-delete-all-cancel`}
              onClick={() => { setConfirmAll(false); setConfirmText(""); }}
              disabled={busy}
              className="btn-secondary text-[11px] py-2 px-4"
            >Cancel</button>
            <button
              data-testid={`${testidPrefix}-delete-all-confirm`}
              onClick={runAll}
              disabled={busy || confirmText !== "DELETE"}
              className="btn-primary text-[11px] py-2 px-4 bg-[#FF1E1E] border-[#FF1E1E] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {busy ? "Deleting…" : `Delete ${totalCount.toLocaleString()} records`}
            </button>
          </div>
        </ConfirmShell>
      )}
    </>
  );
}

function ConfirmShell({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#0E0E0E] border border-[#2A2A2A] p-5 sm:p-6 sm:rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
